import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DIFFICULTIES = {
  easy: { label: "Easy", speed: 0.85, obstacles: 2, targetTime: 40, mathRange: 12 },
  normal: { label: "Normal", speed: 1, obstacles: 4, targetTime: 30, mathRange: 24 },
  hard: { label: "Hard", speed: 1.35, obstacles: 7, targetTime: 22, mathRange: 48 },
};

const randomInt = (max) => Math.floor(Math.random() * max);

const GameButton = ({ children, active = false, ...props }) => (
  <button
    type="button"
    className={`rounded-full px-4 py-2 text-sm font-medium transition ${
      active
        ? "bg-[var(--accent)] text-white shadow-lg shadow-[var(--accent-glow)]"
        : "border border-white/15 bg-white/10 text-skin-text hover:bg-white/15"
    }`}
    {...props}
  >
    {children}
  </button>
);

const Stat = ({ label, value }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
    <div className="text-xs font-semibold uppercase tracking-[0.22em] text-skin-accentSoft">
      {label}
    </div>
    <div className="mt-2 font-display text-2xl font-semibold text-skin-text">{value}</div>
  </div>
);

const TouchControls = ({ onMove, fourWay = false }) => (
  <div className="grid max-w-56 gap-2 sm:hidden" aria-label="Touch game controls">
    {fourWay ? (
      <GameButton onClick={() => onMove("up")}>Up</GameButton>
    ) : null}
    <div className="grid grid-cols-2 gap-2">
      <GameButton onClick={() => onMove("left")}>Left</GameButton>
      <GameButton onClick={() => onMove("right")}>Right</GameButton>
    </div>
    {fourWay ? (
      <GameButton onClick={() => onMove("down")}>Down</GameButton>
    ) : null}
  </div>
);

const RacingGame = ({ difficulty }) => {
  const lanes = 4;
  const settings = DIFFICULTIES[difficulty];
  const [carLane, setCarLane] = useState(1);
  const [obstacles, setObstacles] = useState([{ lane: 0, y: 0, type: "block" }]);
  const [score, setScore] = useState(0);
  const [running, setRunning] = useState(false);
  const [crashed, setCrashed] = useState(false);
  const level = Math.max(1, Math.floor(score / 120) + 1);
  const speed = Math.min(10, level + Math.round(settings.speed * 2));
  const touchStartRef = useRef(null);

  const moveCar = useCallback((direction) => {
    setCarLane((lane) => {
      if (direction === "left") return Math.max(0, lane - 1);
      if (direction === "right") return Math.min(lanes - 1, lane + 1);
      return lane;
    });
  }, []);

  const reset = useCallback(() => {
    setCarLane(1);
    setObstacles([{ lane: randomInt(lanes), y: 0, type: "block" }]);
    setScore(0);
    setCrashed(false);
    setRunning(true);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "ArrowLeft" || event.key.toLowerCase() === "a") {
        event.preventDefault();
        moveCar("left");
      }
      if (event.key === "ArrowRight" || event.key.toLowerCase() === "d") {
        event.preventDefault();
        moveCar("right");
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [moveCar]);

  const handleTouchStart = (event) => {
    const touch = event.changedTouches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event) => {
    if (!touchStartRef.current) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    touchStartRef.current = null;

    if (Math.abs(deltaX) < 24 || Math.abs(deltaX) < Math.abs(deltaY)) return;
    moveCar(deltaX < 0 ? "left" : "right");
  };

  useEffect(() => {
    if (!running || crashed) return undefined;

    const timer = window.setInterval(() => {
      setObstacles((current) => {
        const moved = current
          .map((obstacle) => ({ ...obstacle, y: obstacle.y + 1 }))
          .filter((obstacle) => obstacle.y <= 12);

        if (moved.some((obstacle) => obstacle.y >= 10 && obstacle.y <= 11 && obstacle.lane === carLane)) {
          setCrashed(true);
          setRunning(false);
        }

        const spawnGap = Math.max(1, 5 - Math.min(4, level));
        if (!moved.length || moved[moved.length - 1].y >= spawnGap) {
          const count = level >= 4 ? 2 : 1;
          for (let index = 0; index < count; index += 1) {
            moved.push({
              lane: randomInt(lanes),
              y: 0 - index,
              type: Math.random() > 0.65 ? "oil" : "block",
            });
          }
        }

        return moved;
      });
      setScore((value) => value + 1);
    }, Math.max(90, 380 - speed * 28));

    return () => window.clearInterval(timer);
  }, [carLane, crashed, level, running, speed]);

  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_22rem]">
      <div
        className="relative overflow-hidden rounded-[1.75rem] border border-white/15 bg-slate-950 p-3 shadow-glass sm:p-5"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: "none" }}
      >
        <div
          className="mx-auto grid max-w-2xl gap-2 rounded-2xl border border-white/10 bg-slate-900 p-3"
          style={{
            gridTemplateColumns: `repeat(${lanes}, minmax(0, 1fr))`,
            height: "min(34rem, 68vh)",
            minHeight: "24rem",
          }}
        >
          {Array.from({ length: lanes }, (_, lane) => (
            <div key={lane} className="relative rounded-xl border-x border-dashed border-white/15 bg-white/[0.03]">
              {obstacles
                .filter((obstacle) => obstacle.lane === lane)
                .map((obstacle, index) => (
                  <div
                    key={`${obstacle.lane}-${obstacle.y}-${index}`}
                    className={`absolute left-1/2 -translate-x-1/2 shadow-lg ${
                      obstacle.type === "oil"
                        ? "h-8 w-16 rounded-full bg-fuchsia-500/80 shadow-fuchsia-500/30"
                        : "h-12 w-14 rounded-lg bg-amber-400 shadow-amber-500/30"
                    }`}
                    style={{ top: `${obstacle.y * 8}%` }}
                  />
                ))}
              {carLane === lane ? (
                <div className="absolute bottom-6 left-1/2 h-16 w-12 -translate-x-1/2 rounded-xl bg-cyan-400 shadow-lg shadow-cyan-500/40">
                  <div className="mx-auto mt-2 h-5 w-7 rounded bg-slate-900/70" />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-4 rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
        <Stat label="Score" value={score} />
        <Stat label="Level" value={level} />
        <Stat label="Speed" value={speed} />
        <p className="text-sm leading-7 text-skin-muted">
          Move with left/right arrows, A/D, swipe, or touch buttons. Higher levels add faster traffic and more road hazards.
        </p>
        <TouchControls onMove={moveCar} />
        <div className="flex flex-wrap gap-3">
          <GameButton onClick={() => setRunning((value) => !value)}>
            {running ? "Pause" : "Start"}
          </GameButton>
          <GameButton onClick={reset}>Restart</GameButton>
        </div>
        {crashed ? <div className="rounded-2xl border border-amber-300/40 bg-amber-300/10 p-4 text-sm text-amber-200">Race ended. Restart to try again.</div> : null}
      </div>
    </div>
  );
};

const makeObstacles = (size, count, snake = [], food = null) => {
  const blocked = new Set(snake.map((part) => `${part.x}-${part.y}`));
  if (food) blocked.add(`${food.x}-${food.y}`);
  const obstacles = [];

  while (obstacles.length < count) {
    const next = { x: randomInt(size), y: randomInt(size) };
    const key = `${next.x}-${next.y}`;
    if (!blocked.has(key) && !obstacles.some((item) => item.x === next.x && item.y === next.y)) {
      obstacles.push(next);
    }
  }

  return obstacles;
};

const SnakeGame = ({ difficulty }) => {
  const size = 18;
  const settings = DIFFICULTIES[difficulty];
  const [snake, setSnake] = useState([{ x: 8, y: 8 }]);
  const [direction, setDirection] = useState({ x: 1, y: 0 });
  const directionRef = useRef(direction);
  const [food, setFood] = useState({ x: 12, y: 8 });
  const [obstacles, setObstacles] = useState(() => makeObstacles(size, settings.obstacles));
  const snakeRef = useRef(snake);
  const foodRef = useRef(food);
  const touchStartRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const level = Math.max(1, Math.floor((snake.length - 1) / 4) + 1);

  useEffect(() => {
    directionRef.current = direction;
  }, [direction]);

  useEffect(() => {
    snakeRef.current = snake;
  }, [snake]);

  useEffect(() => {
    foodRef.current = food;
  }, [food]);

  const placeFood = useCallback((currentSnake = snake, currentObstacles = obstacles) => {
    const occupied = new Set([
      ...currentSnake.map((part) => `${part.x}-${part.y}`),
      ...currentObstacles.map((part) => `${part.x}-${part.y}`),
    ]);
    let next = { x: 0, y: 0 };

    do {
      next = { x: randomInt(size), y: randomInt(size) };
    } while (occupied.has(`${next.x}-${next.y}`));
    setFood(next);
  }, [obstacles, snake]);

  const requestDirection = useCallback((next) => {
    const current = directionRef.current;
    if (current.x + next.x !== 0 || current.y + next.y !== 0) {
      directionRef.current = next;
      setDirection(next);
    }
  }, []);

  const moveSnake = useCallback((name) => {
    const next = {
      up: { x: 0, y: -1 },
      down: { x: 0, y: 1 },
      left: { x: -1, y: 0 },
      right: { x: 1, y: 0 },
    }[name];

    if (next) requestDirection(next);
  }, [requestDirection]);

  const reset = useCallback(() => {
    const startSnake = [{ x: 8, y: 8 }];
    const startFood = { x: 12, y: 8 };
    setSnake(startSnake);
    setDirection({ x: 1, y: 0 });
    setFood(startFood);
    setObstacles(makeObstacles(size, settings.obstacles, startSnake, startFood));
    setGameOver(false);
    setRunning(true);
  }, [settings.obstacles]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const next = {
        ArrowUp: { x: 0, y: -1 },
        ArrowDown: { x: 0, y: 1 },
        ArrowLeft: { x: -1, y: 0 },
        ArrowRight: { x: 1, y: 0 },
        w: { x: 0, y: -1 },
        s: { x: 0, y: 1 },
        a: { x: -1, y: 0 },
        d: { x: 1, y: 0 },
      }[event.key.length === 1 ? event.key.toLowerCase() : event.key];

      if (!next) return;
      event.preventDefault();
      requestDirection(next);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [requestDirection]);

  const handleTouchStart = (event) => {
    const touch = event.changedTouches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event) => {
    if (!touchStartRef.current) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    touchStartRef.current = null;

    if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 24) return;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      moveSnake(deltaX < 0 ? "left" : "right");
      return;
    }
    moveSnake(deltaY < 0 ? "up" : "down");
  };

  useEffect(() => {
    setObstacles(makeObstacles(size, settings.obstacles + level - 1, snakeRef.current, foodRef.current));
  }, [level, settings.obstacles]);

  useEffect(() => {
    if (!running || gameOver) return undefined;

    const timer = window.setInterval(() => {
      setSnake((current) => {
        const head = current[0];
        const nextHead = {
          x: head.x + directionRef.current.x,
          y: head.y + directionRef.current.y,
        };

        const hitWall = nextHead.x < 0 || nextHead.x >= size || nextHead.y < 0 || nextHead.y >= size;
        const hitSelf = current.some((part) => part.x === nextHead.x && part.y === nextHead.y);
        const hitObstacle = obstacles.some((part) => part.x === nextHead.x && part.y === nextHead.y);

        if (hitWall || hitSelf || hitObstacle) {
          setGameOver(true);
          setRunning(false);
          return current;
        }

        const nextSnake = [nextHead, ...current];
        if (nextHead.x === food.x && nextHead.y === food.y) {
          placeFood(nextSnake, obstacles);
          return nextSnake;
        }

        nextSnake.pop();
        return nextSnake;
      });
    }, Math.max(70, 190 - snake.length * 3 - settings.speed * 28));

    return () => window.clearInterval(timer);
  }, [food, gameOver, obstacles, placeFood, running, settings.speed, snake.length]);

  const cells = useMemo(() => Array.from({ length: size * size }, (_, index) => ({
    x: index % size,
    y: Math.floor(index / size),
  })), []);

  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_22rem]">
      <div
        className="rounded-[1.75rem] border border-white/15 bg-slate-950 p-3 shadow-glass sm:p-5"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: "none" }}
      >
        <div className="mx-auto grid max-w-[34rem] gap-1" style={{ gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))` }}>
          {cells.map((cell) => {
            const isSnake = snake.some((part) => part.x === cell.x && part.y === cell.y);
            const isFood = food.x === cell.x && food.y === cell.y;
            const isObstacle = obstacles.some((part) => part.x === cell.x && part.y === cell.y);
            return (
              <div
                key={`${cell.x}-${cell.y}`}
                className={`aspect-square rounded ${
                  isSnake ? "bg-cyan-400" : isFood ? "bg-amber-300" : isObstacle ? "bg-rose-500" : "bg-white/[0.04]"
                }`}
              />
            );
          })}
        </div>
      </div>
      <div className="space-y-4 rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
        <Stat label="Length" value={snake.length} />
        <Stat label="Level" value={level} />
        <Stat label="Obstacles" value={obstacles.length} />
        <p className="text-sm leading-7 text-skin-muted">Use arrows, WASD, swipe, or touch buttons. Red blocks are obstacles; they increase as levels rise.</p>
        <TouchControls onMove={moveSnake} fourWay />
        <div className="flex flex-wrap gap-3">
          <GameButton onClick={() => setRunning((value) => !value)}>
            {running ? "Pause" : "Start"}
          </GameButton>
          <GameButton onClick={reset}>Restart</GameButton>
        </div>
        {gameOver ? <div className="rounded-2xl border border-amber-300/40 bg-amber-300/10 p-4 text-sm text-amber-200">Game over. Restart for a new run.</div> : null}
      </div>
    </div>
  );
};

const TargetGame = ({ difficulty }) => {
  const settings = DIFFICULTIES[difficulty];
  const [target, setTarget] = useState({ x: 45, y: 45, size: 64 });
  const [score, setScore] = useState(0);
  const [misses, setMisses] = useState(0);
  const [timeLeft, setTimeLeft] = useState(settings.targetTime);
  const [running, setRunning] = useState(false);
  const level = Math.max(1, Math.floor(score / 6) + 1);

  const moveTarget = useCallback((nextScore = score) => {
    setTarget({
      x: 8 + Math.random() * 78,
      y: 8 + Math.random() * 78,
      size: Math.max(26, 70 - nextScore * 2 - settings.obstacles),
    });
  }, [score, settings.obstacles]);

  const reset = useCallback(() => {
    setScore(0);
    setMisses(0);
    setTimeLeft(settings.targetTime);
    setRunning(true);
    setTarget({ x: 45, y: 45, size: 64 });
  }, [settings.targetTime]);

  useEffect(() => {
    setTimeLeft(settings.targetTime);
  }, [settings.targetTime]);

  useEffect(() => {
    if (!running) return undefined;

    const timer = window.setInterval(() => {
      setTimeLeft((value) => {
        if (value <= 1) {
          setRunning(false);
          return 0;
        }
        return value - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [running]);

  const hitTarget = (event) => {
    event.stopPropagation();
    if (!running) return;
    setScore((value) => {
      const next = value + 1;
      moveTarget(next);
      return next;
    });
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_22rem]">
      <button
        type="button"
        className="relative h-[34rem] overflow-hidden rounded-[1.75rem] border border-white/15 bg-[radial-gradient(circle_at_center,#1e293b,#020617)] shadow-glass"
        onClick={() => running && setMisses((value) => value + 1)}
        style={{ height: "min(34rem, 68vh)", minHeight: "24rem", touchAction: "manipulation" }}
      >
        {Array.from({ length: Math.min(6, level + settings.obstacles - 2) }, (_, index) => (
          <span
            key={index}
            className="absolute rounded-full border border-white/20 bg-slate-500/50"
            style={{
              left: `${12 + ((index * 17 + level * 9) % 76)}%`,
              top: `${16 + ((index * 23 + level * 7) % 68)}%`,
              width: 36,
              height: 36,
            }}
          />
        ))}
        <span
          className="absolute grid place-items-center rounded-full border-4 border-white bg-rose-500 shadow-[0_0_40px_rgba(244,63,94,0.45)]"
          style={{
            left: `${target.x}%`,
            top: `${target.y}%`,
            width: target.size,
            height: target.size,
            transform: "translate(-50%, -50%)",
          }}
          onClick={hitTarget}
        >
          <span className="h-1/2 w-1/2 rounded-full bg-white">
            <span className="m-auto block h-1/2 w-1/2 translate-y-1/2 rounded-full bg-rose-500" />
          </span>
        </span>
      </button>
      <div className="space-y-4 rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
        <Stat label="Score" value={score} />
        <Stat label="Level" value={level} />
        <Stat label="Time" value={`${timeLeft}s`} />
        <Stat label="Misses" value={misses} />
        <p className="text-sm leading-7 text-skin-muted">Click the red target. Higher levels shrink the target and add visual obstacles.</p>
        <div className="flex flex-wrap gap-3">
          <GameButton onClick={() => setRunning((value) => !value)}>
            {running ? "Pause" : "Start"}
          </GameButton>
          <GameButton onClick={reset}>Restart</GameButton>
        </div>
      </div>
    </div>
  );
};

const MemoryGame = ({ difficulty }) => {
  const colors = ["bg-cyan-400", "bg-amber-300", "bg-rose-400", "bg-emerald-400", "bg-violet-400", "bg-orange-400"];
  const colorNames = ["Blue", "Yellow", "Red", "Green", "Purple", "Orange"];
  const settings = DIFFICULTIES[difficulty];
  const [sequence, setSequence] = useState([]);
  const [inputIndex, setInputIndex] = useState(0);
  const [message, setMessage] = useState("Press Start, read the pattern, then click the colors in the same order.");
  const [level, setLevel] = useState(1);

  const start = () => {
    setLevel(1);
    setInputIndex(0);
    setSequence([randomInt(settings.obstacles >= 7 ? 6 : 4)]);
    setMessage("Click color 1 in the shown pattern.");
  };

  const pick = (index) => {
    if (!sequence.length) {
      setMessage("Press Start first to create a pattern.");
      return;
    }
    if (sequence[inputIndex] !== index) {
      setMessage(`Missed. Expected ${colorNames[sequence[inputIndex]]}. Press Start / Restart for a new pattern.`);
      setInputIndex(0);
      return;
    }

    if (inputIndex === sequence.length - 1) {
      setLevel((value) => value + 1);
      setInputIndex(0);
      setSequence((current) => [...current, randomInt(settings.obstacles >= 7 ? 6 : 4)]);
      setMessage("Correct. A new color was added. Repeat the full pattern from the beginning.");
      return;
    }

    setInputIndex((value) => value + 1);
    setMessage(`Good. Next click step ${inputIndex + 2} of ${sequence.length}.`);
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_22rem]">
      <div className="rounded-[1.75rem] border border-white/15 bg-slate-950 p-5 shadow-glass">
        <div className="mb-5 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm leading-7 text-skin-muted">
          Watch the numbered pattern below. Click the colored tiles in that exact order. Each completed round adds one more color.
        </div>
        <div className="mx-auto grid max-w-2xl grid-cols-2 gap-4 md:grid-cols-3">
          {colors.slice(0, settings.obstacles >= 7 ? 6 : 4).map((color, index) => (
            <button
              key={color}
              type="button"
              className={`grid aspect-square place-items-center rounded-3xl border border-white/20 ${color} font-display text-3xl font-bold text-slate-950 shadow-glass transition hover:scale-[1.02]`}
              onClick={() => pick(index)}
            >
              {index + 1}
            </button>
          ))}
        </div>
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 font-mono text-sm text-skin-text">
          Pattern: {sequence.length ? sequence.map((item) => `${item + 1}:${colorNames[item]}`).join("  >  ") : "Press Start to generate a pattern"}
        </div>
      </div>
      <div className="space-y-4 rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
        <Stat label="Level" value={level} />
        <Stat label="Steps" value={sequence.length} />
        <Stat label="Next Step" value={sequence.length ? `${inputIndex + 1}/${sequence.length}` : "0/0"} />
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-skin-accentSoft">
            How to play
          </div>
          <ol className="list-decimal space-y-2 pl-5 text-sm leading-6 text-skin-muted">
            <li>Press Start to create a color pattern.</li>
            <li>Read the pattern shown under the tiles.</li>
            <li>Click the tiles in the same order.</li>
            <li>After a correct round, the pattern gets one step longer.</li>
          </ol>
        </div>
        <p className="text-sm leading-7 text-skin-muted">{message}</p>
        <GameButton onClick={start}>Start / Restart</GameButton>
      </div>
    </div>
  );
};

const makeMathQuestion = (range) => {
  const a = 1 + randomInt(range);
  const b = 1 + randomInt(range);
  const ops = ["+", "-", "x"];
  const op = ops[randomInt(ops.length)];
  const answer = op === "+" ? a + b : op === "-" ? a - b : a * b;
  return { text: `${a} ${op} ${b}`, answer };
};

const MathSprintGame = ({ difficulty }) => {
  const settings = DIFFICULTIES[difficulty];
  const [question, setQuestion] = useState(() => makeMathQuestion(settings.mathRange));
  const [answer, setAnswer] = useState("");
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(45);
  const [running, setRunning] = useState(false);
  const level = Math.max(1, Math.floor(score / 5) + 1);

  const reset = () => {
    setQuestion(makeMathQuestion(settings.mathRange));
    setAnswer("");
    setScore(0);
    setTimeLeft(45);
    setRunning(true);
  };

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => {
      setTimeLeft((value) => {
        if (value <= 1) {
          setRunning(false);
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  const submit = (event) => {
    event.preventDefault();
    if (!running) return;
    if (Number(answer) === question.answer) {
      setScore((value) => value + 1);
      setQuestion(makeMathQuestion(settings.mathRange + level * 5));
      setAnswer("");
    }
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_22rem]">
      <form
        onSubmit={submit}
        className="grid h-[34rem] place-items-center rounded-[1.75rem] border border-white/15 bg-slate-950 p-5 shadow-glass"
        style={{ height: "min(34rem, 68vh)", minHeight: "24rem" }}
      >
        <div className="w-full max-w-xl text-center">
          <div className="font-display text-6xl font-bold text-skin-text">{question.text}</div>
          <input
            className="mt-8 w-full rounded-3xl border border-white/15 bg-black/30 px-6 py-5 text-center font-mono text-4xl text-skin-text outline-none focus:border-[var(--accent)]"
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
            inputMode="numeric"
          />
          <button type="submit" className="mt-5 rounded-full bg-[var(--accent)] px-6 py-3 text-sm font-semibold text-white">
            Submit
          </button>
        </div>
      </form>
      <div className="space-y-4 rounded-[1.75rem] border border-white/15 bg-black/10 p-5">
        <Stat label="Score" value={score} />
        <Stat label="Level" value={level} />
        <Stat label="Time" value={`${timeLeft}s`} />
        <p className="text-sm leading-7 text-skin-muted">Solve as many quick math prompts as possible before time runs out.</p>
        <div className="flex flex-wrap gap-3">
          <GameButton onClick={() => setRunning((value) => !value)}>
            {running ? "Pause" : "Start"}
          </GameButton>
          <GameButton onClick={reset}>Restart</GameButton>
        </div>
      </div>
    </div>
  );
};

const SinglePlayerGames = () => {
  const [activeGame, setActiveGame] = useState("racing");
  const [difficulty, setDifficulty] = useState("normal");
  const [fullscreen, setFullscreen] = useState(false);
  const shellRef = useRef(null);
  const games = [
    ["racing", "Racing"],
    ["snake", "Snake"],
    ["target", "Hunting"],
    ["memory", "Memory"],
    ["math", "Math Sprint"],
  ];

  useEffect(() => {
    const onFullscreenChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await shellRef.current?.requestFullscreen?.();
    } else {
      await document.exitFullscreen?.();
    }
  };

  return (
    <section
      ref={shellRef}
      className="rounded-[2rem] border border-white/15 bg-white/10 p-6 shadow-glass backdrop-blur-xl md:p-8 fullscreen:overflow-auto fullscreen:bg-[#07111f]"
    >
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.32em] text-skin-accentSoft">
            Single Player Games
          </p>
          <h1 className="font-display text-3xl font-bold text-skin-text md:text-4xl">
            Arcade and brain games with levels and difficulty
          </h1>
        </div>
        <p className="max-w-2xl text-sm leading-7 text-skin-muted">
          Racing, snake, target practice, memory, and math sprint with obstacles, levels, and fullscreen mode.
        </p>
      </div>

      <div className="mb-5 flex flex-wrap gap-3">
        {games.map(([key, label]) => (
          <GameButton key={key} active={activeGame === key} onClick={() => setActiveGame(key)}>
            {label}
          </GameButton>
        ))}
      </div>

      <div className="mb-8 flex flex-wrap items-center gap-3">
        {Object.entries(DIFFICULTIES).map(([key, item]) => (
          <GameButton key={key} active={difficulty === key} onClick={() => setDifficulty(key)}>
            {item.label}
          </GameButton>
        ))}
        <GameButton onClick={toggleFullscreen}>
          {fullscreen ? "Exit Fullscreen" : "Fullscreen"}
        </GameButton>
      </div>

      {activeGame === "racing" ? <RacingGame difficulty={difficulty} /> : null}
      {activeGame === "snake" ? <SnakeGame difficulty={difficulty} /> : null}
      {activeGame === "target" ? <TargetGame difficulty={difficulty} /> : null}
      {activeGame === "memory" ? <MemoryGame difficulty={difficulty} /> : null}
      {activeGame === "math" ? <MathSprintGame difficulty={difficulty} /> : null}
    </section>
  );
};

export default SinglePlayerGames;
